import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { Share2, Instagram, Linkedin, Twitter, ExternalLink } from "lucide-react";

const Social = () => {
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });

  const socialPlatforms = [
    {
      name: "LinkedIn",
      icon: Linkedin,
      handle: "@harsh-sanghani",
      followers: "10K+",
      description: "Professional updates and global leadership insights",
      color: "bg-blue-600",
      link: "https://www.linkedin.com/in/harsh-sanghani"
    },
    {
      name: "Instagram",
      icon: Instagram,
      handle: "@harsh_mukeshbhai_sanghani",
      followers: "5K+",
      description: "Behind the scenes of global initiatives",
      color: "bg-gradient-to-br from-purple-600 to-pink-600",
      link: "https://www.instagram.com/harsh_mukeshbhai_sanghani"
    },
    {
      name: "Twitter",
      icon: Twitter,
      handle: "@HarshSanghani99",
      followers: "3K+",
      description: "Real-time updates and thought leadership",
      color: "bg-sky-500",
      link: "https://x.com/HarshSanghani99"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-red-900 to-red-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <Share2 className="w-16 h-16 text-white mx-auto mb-6" />
            <h1 className="text-5xl font-bold text-white mb-4">Social Media</h1>
            <p className="text-xl text-red-100 max-w-2xl mx-auto">
              Connect and engage across platforms
            </p>
          </motion.div>
        </div>
      </section>

      {/* Social Platforms */}
      <section ref={ref} className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {socialPlatforms.map((platform, index) => (
              <motion.div
                key={platform.name}
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
              >
                <div className={`${platform.color} p-8 text-center`}>
                  <platform.icon className="w-16 h-16 text-white mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-white mb-2">{platform.name}</h3>
                  <p className="text-white/90">{platform.handle}</p>
                </div>
                <div className="p-6">
                  <div className="text-center mb-4">
                    <div className="text-3xl font-bold text-gray-900">{platform.followers}</div>
                    <div className="text-sm text-gray-500">Followers</div>
                  </div>
                  <p className="text-gray-600 text-center mb-6">{platform.description}</p>
                  <a
                    href={platform.link}
                    className="w-full inline-flex items-center justify-center px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors font-medium"
                  >
                    Follow <ExternalLink className="w-4 h-4 ml-2" />
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Join the <span className="text-red-600">Conversation</span>
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              Stay updated with the latest insights, initiatives, and global impact stories
            </p>
            <div className="flex justify-center gap-4">
              {socialPlatforms.map((platform) => (
                <a
                  key={platform.name}
                  href={platform.link}
                  className="p-4 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors"
                >
                  <platform.icon className="w-6 h-6 text-gray-700" />
                </a>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Social;
